import React, {useCallback, useState} from 'react';

import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {WidthItem} from 'src/components/width-item';
import {useAccountStatuses} from 'src/hooks/dictionaries';
import {AccountStatus as AccountStatuses} from 'src/openapi/admin/models/AccountStatus';

import Button from '@pb/kit-component-button';
import {ComboBox, ComboboxItem} from '@pb/kit-component-combo-box';
import {DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import {TOOL_TITLE} from './constants';

import type {ProcessBulkAction} from 'src/components/bulk-tool/typedef';
import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {AccountStatus} from 'src/openapi/admin/models/AccountStatus';
import type {UpdateAccountStatusRequest} from 'src/openapi/admin/models/UpdateAccountStatusRequest';

type Props = {
    accountIds: Array<number>,
    onCloseModal: (submit: boolean) => void,
    processAction: ProcessBulkAction<UpdateAccountStatusRequest>,
};

export const UpdateAccountStatusModal: React.FC<Props> = ({accountIds, onCloseModal, processAction}: Props) => {
    const {getAccountStatusByCode} = useAccountStatuses();
    const [selectedAccountStatus, setSelectedAccountStatus] = useState<AccountStatus | null>(null);

    const updateStatus = useCallback(async (options?: ActionOptions) => {
        if (selectedAccountStatus) {
            return await processAction({
                accountsId: accountIds,
                status: selectedAccountStatus,
            }, options);
        }
    }, [selectedAccountStatus, accountIds, processAction]);

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>
                {TOOL_TITLE}
            </DialogHeader>
            <DialogMain style={{padding: 24, maxWidth: 'fit-content'}}>
                <Group direction="v">
                    <WidthItem width="100%">
                        Update the status for selected accounts.
                    </WidthItem>
                    <WidthItem width={350}>
                        <Label>Account status</Label>
                        <ComboBox
                            placeholder="Select account status"
                            onChange={setSelectedAccountStatus}
                            value={selectedAccountStatus}>
                            {Object.values(AccountStatuses).map(code => (
                                <ComboboxItem
                                    key={code}
                                    caption={getAccountStatusByCode(code)?.description || ''}
                                    value={code}
                                />
                            ))}
                        </ComboBox>
                    </WidthItem>
                </Group>
            </DialogMain>
            <BulkActionFooter action="Update" onConfirm={updateStatus} disabled={!selectedAccountStatus || !accountIds.length}>
                <Group>

                    <Button variant="Type.SECONDARY" onClick={() => onCloseModal(false)}>
                        Cancel
                    </Button>
                </Group>
            </BulkActionFooter>
        </>
    );
};
